"use client"

import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import WorkflowService from '@/api/workflow.service';
import { Loader2, Plus, Settings, Activity, Play, PauseCircle, FileCode, Edit, Trash2, ArrowRight, Code } from 'lucide-react';
import { 
  Card, 
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Switch } from '@/components/ui/switch';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import FormModal from '@/components/FormModal';
import CreateWorkflowForm from './CreateWorkflowForm';

export default function Workflows() {
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<number | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  const { toast } = useToast();
  
  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
  const businessId = selectedBusiness?.id;
  
  useEffect(() => {
    if (businessId) {
      fetchWorkflows();
    }
  }, [businessId]);
  
  const fetchWorkflows = async () => {
    setIsLoading(true);
    try {
      const response = await WorkflowService.getAllWorkflows(businessId);
      
      if (response.data.result === 'success') {
        setWorkflows(response.data.workflows || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch workflows",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching workflows:", error);
      toast({
        title: "Error",
        description: "An error occurred while fetching workflows",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteWorkflow = async () => {
    if (!workflowToDelete) return;
    
    try {
      const response = await WorkflowService.deleteWorkflow(workflowToDelete);
      
      if (response.data.result === 'success') {
        setWorkflows(workflows.filter(workflow => workflow.id !== workflowToDelete));
        
        toast({
          title: "Success",
          description: "Workflow deleted successfully",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete workflow",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting workflow:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the workflow",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };
  
  const toggleWorkflowStatus = async (workflowId: number, currentStatus: boolean) => {
    try {
      const response = currentStatus 
        ? await WorkflowService.disableWorkflow(workflowId)
        : await WorkflowService.enableWorkflow(workflowId);
      
      if (response.data.result === 'success') {
        // Optimistic update
        const updatedWorkflows = workflows.map(workflow => 
          workflow.id === workflowId 
            ? { ...workflow, isActive: !currentStatus } 
            : workflow
        );
        setWorkflows(updatedWorkflows);
        
        toast({
          title: "Success",
          description: `Workflow ${currentStatus ? 'disabled' : 'enabled'} successfully`,
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message || `Failed to ${currentStatus ? 'disable' : 'enable'} workflow`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(`Error ${currentStatus ? 'disabling' : 'enabling'} workflow:`, error);
      toast({
        title: "Error",
        description: `An error occurred while ${currentStatus ? 'disabling' : 'enabling'} the workflow`,
        variant: "destructive",
      });
    }
  };
  
  const getTriggerIcon = (triggerType: string) => {
    switch (triggerType) {
      case 'customer_created':
        return <Plus className="w-4 h-4 text-green-500" />;
      case 'deal_stage_changed':
        return <ArrowRight className="w-4 h-4 text-blue-500" />;
      case 'reminder_due':
        return <Activity className="w-4 h-4 text-purple-500" />;
      default:
        return <Code className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const getTriggerDescription = (triggerType: string) => {
    switch (triggerType) {
      case 'customer_created':
        return 'When a new customer is created';
      case 'deal_stage_changed':
        return 'When a deal changes stage';
      case 'reminder_due':
        return 'When a reminder is due';
      default:
        return triggerType.replace(/_/g, ' ');
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading workflows...</p>
      </div>
    );
  }
  
  return (
    <div className="container py-6 mx-auto">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Automated Workflows</h1>
            <p className="text-muted-foreground mt-1">Automate your business processes with custom workflows</p>
          </div>
          
          <Button 
            onClick={() => {
              setSelectedWorkflow(null);
              setIsCreateModalOpen(true);
            }}
            className="gap-1"
          >
            <Plus className="w-4 h-4" />
            Create Workflow
          </Button>
        </div>
        
        {/* Workflows List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your Workflows</CardTitle>
            <CardDescription>
              Manage your automated processes and actions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {workflows.length === 0 ? (
              <div className="text-center py-8">
                <Settings className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Workflows Found</h3>
                <p className="text-muted-foreground mb-6">
                  You haven't created any automated workflows yet.
                </p>
                <Button onClick={() => setIsCreateModalOpen(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Workflow
                </Button>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Name</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead className="w-[140px]">Created</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[100px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workflows.map((workflow) => (
                      <TableRow key={workflow.id}>
                        <TableCell className="font-medium">
                          {workflow.name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTriggerIcon(workflow.triggerType)}
                            <span>{getTriggerDescription(workflow.triggerType)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {format(new Date(workflow.createdAt), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={workflow.isActive}
                              onCheckedChange={() => toggleWorkflowStatus(workflow.id, workflow.isActive)}
                            />
                            <span className={workflow.isActive ? 'text-green-600' : 'text-muted-foreground'}>
                              {workflow.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="w-4 h-4"
                                >
                                  <circle cx="12" cy="12" r="1" />
                                  <circle cx="12" cy="5" r="1" />
                                  <circle cx="12" cy="19" r="1" />
                                </svg>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                className="flex items-center gap-2"
                                onClick={() => {
                                  setSelectedWorkflow(workflow);
                                  setIsCreateModalOpen(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="flex items-center gap-2"
                                onClick={() => {/* View logs */}}
                              >
                                <FileCode className="w-4 h-4" />
                                View Logs
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="flex items-center gap-2 text-destructive focus:text-destructive"
                                onClick={() => {
                                  setWorkflowToDelete(workflow.id);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Create/Edit Workflow Modal */}
      <FormModal
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        title={selectedWorkflow ? "Edit Workflow" : "Create New Workflow"}
      >
        <CreateWorkflowForm 
          businessId={businessId} 
          workflow={selectedWorkflow}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchWorkflows();
          }}
        />
      </FormModal>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the workflow and all its data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWorkflow}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 